import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Chips from "../chips/Chips";
import { experience } from "../../data/experience";
import { skills } from "../../data/skills";
import { fadeUpItem, staggerContainer } from "../../animations/variants";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import "./Hero.css";

// Professional React/TypeScript experience is counted from 2022 (Accenture);
// the earlier 2021 role uses a different stack and is intentionally excluded
// from the "Years" stat. Keep the lead copy ("4 years" / "4 anni") in sync.
const START_YEAR = 2022;

function Hero() {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const currentRole = experience.find((entry) => !entry.endYear) ?? experience[0];
  const yearsOfExperience = new Date().getFullYear() - START_YEAR;

  const container = prefersReducedMotion ? {} : staggerContainer(0.09);
  const item = prefersReducedMotion ? {} : fadeUpItem;

  return (
    <section id="hero" className="hero">
      <div className="hero-decor" aria-hidden="true">
        <span className="hero-glow" />
        <span className="hero-codemark hero-codemark-1">&lt;/&gt;</span>
        <span className="hero-codemark hero-codemark-2">{"{ }"}</span>
        <span className="hero-codemark hero-codemark-3">
          <svg width="150" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="M6 9l4 3-4 3" />
            <path d="M13 15h5" />
          </svg>
        </span>
      </div>

      <motion.div
        className="hero-grid"
        initial="hidden"
        animate="visible"
        variants={container}
      >
        <div className="hero-main">
          <motion.p className="hero-eyebrow" variants={item}>
            {t("hero.eyebrow")}
          </motion.p>
          <motion.h1 className="hero-title" variants={item}>
            {t("hero.titleLine1")}
            <br />
            {t("hero.titleLine2Before")}{" "}
            <span className="hero-accent">{t("hero.titleAccent")}</span>.
          </motion.h1>
          <motion.p className="hero-lead" variants={item}>
            {t("hero.lead")}
          </motion.p>
          <motion.div className="hero-cta-row" variants={item}>
            <a href="#contact" className="btn-primary">
              {t("hero.ctaPrimary")}
            </a>
            <a href="#experience" className="btn-secondary">
              {t("hero.ctaSecondary")} <ArrowRight size={16} aria-hidden="true" />
            </a>
          </motion.div>
          <motion.div className="hero-stat-row" variants={item}>
            <div className="hero-stat">
              <b>{String(yearsOfExperience).padStart(2, "0")}</b>
              <span>{t("hero.statYears")}</span>
            </div>
            <div className="hero-stat">
              <b>{String(experience.length).padStart(2, "0")}</b>
              <span>{t("hero.statCompanies")}</span>
            </div>
            <div className="hero-stat">
              <b>{skills.length}+</b>
              <span>{t("hero.statTech")}</span>
            </div>
          </motion.div>
        </div>

        <motion.div className="hero-card" variants={item}>
          <div className="hero-card-top">
            <div className="hero-avatar">YB</div>
            <div>
              <span className="hero-card-name">{t("hero.cardName")}</span>
              <span className="hero-card-role">{t("hero.cardRole")}</span>
            </div>
          </div>
          <div className="hero-status">
            <i />
            {t("hero.statusCurrent")} — {currentRole.company}
          </div>
          <Chips chips={currentRole.chips} />
        </motion.div>
      </motion.div>
    </section>
  );
}

export default Hero;
